import { Component, OnInit, computed, signal } from '@angular/core';
import { RouterLink } from '@angular/router';
import { PermitUserService } from '../../core/api/permit-user.service';
import { PermitUser } from '../../shared/models/permit-user';
import { AppNavComponent } from '../../shared/app-nav.component';
import { BackButtonComponent } from '../../shared/back-button.component';
import { TranslatePipe } from '../../shared/translate.pipe';
import { MneDatePipe } from '../../shared/mne-date.pipe';

type StatusFilter = '' | 'active' | 'blocked' | 'blacklisted';

@Component({
  standalone: true,
  imports: [RouterLink, AppNavComponent, BackButtonComponent, TranslatePipe, MneDatePipe],
  template: `
        <app-nav></app-nav>
    <main class="page-shell">
      <header class="topbar">
        <app-back></app-back>
        <h1>{{ 'permit.title' | t }}</h1>
      </header>

      <div class="search-bar">
        <input type="search" [placeholder]="'common.search' | t" [value]="query()" (input)="query.set($any($event.target).value)">
      </div>

      <div class="filter-bar">
        <select [value]="statusFilter()" (change)="statusFilter.set($any($event.target).value)">
          <option value="">{{ 'filters.allStatuses' | t }}</option>
          <option value="active">{{ 'permit.active' | t }}</option>
          <option value="blocked">{{ 'status.blocked' | t }}</option>
          <option value="blacklisted">{{ 'status.blacklisted' | t }}</option>
        </select>
        <select [value]="companyFilter()" (change)="companyFilter.set($any($event.target).value)">
          <option value="">{{ 'filters.allCompanies' | t }}</option>
          @for (c of companyOptions(); track c) { <option [value]="c">{{ c }}</option> }
        </select>
        @if (statusFilter() || companyFilter() || query()) {
          <button type="button" class="filter-clear" (click)="clearFilters()">{{ 'filters.clear' | t }}</button>
        }
      </div>

      @if (loading()) {
        <div class="skeleton-list">
          @for (i of [1,2,3,4,5,6]; track i) { <div class="skeleton-row"></div> }
        </div>
      }
      @if (error()) { <p class="error notice">{{ error() | t }}</p> }
      <section class="list">
        @for (item of filtered(); track item.Id) {
          <div class="list-row static">
            <div>
              <strong>{{ item.FullName || (('permit.fallback' | t) + ' #' + item.Id) }}</strong>
              <span>{{ item.CompanyName || '' }}</span>
            </div>
            <div class="row-meta">
              @if (item.Blocked) { <span class="status-pill danger">{{ 'status.blocked' | t }}</span> }
              @else if (item.Blacklist) { <span class="status-pill danger">{{ 'status.blacklisted' | t }}</span> }
              @else { <span>{{ item.BadgeValidTo ? (('permit.validTo' | t) + ' ' + (item.BadgeValidTo | mneDate:false)) : '' }}</span> }
            </div>
          </div>
        } @empty { @if (!loading() && !error()) {
          <div class="empty-state">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M4 7h16v13H4z"/><path d="M4 7l3-4h10l3 4"/><path d="M9 11h6"/></svg>
            <span>{{ 'permit.empty' | t }}</span>
          </div>
        } }
      </section>
    </main>
  `
})
export class PermitsPage implements OnInit {
  readonly items = signal<PermitUser[]>([]);
  readonly loading = signal(true);
  readonly error = signal('');
  readonly query = signal('');
  readonly statusFilter = signal<StatusFilter>('');
  readonly companyFilter = signal('');

  readonly companyOptions = computed(() =>
    Array.from(new Set(this.items().map(i => i.CompanyName).filter((v): v is string => !!v))).sort()
  );

  readonly filtered = computed(() => {
    const q = this.query().trim().toLowerCase();
    const status = this.statusFilter();
    const company = this.companyFilter();
    return this.items().filter(item => {
      if (company && item.CompanyName !== company) return false;
      if (status === 'blocked' && !item.Blocked) return false;
      if (status === 'blacklisted' && !item.Blacklist) return false;
      if (status === 'active' && (item.Blocked || item.Blacklist)) return false;
      if (!q) return true;
      return (item.FullName ?? '').toLowerCase().includes(q) ||
        (item.CompanyName ?? '').toLowerCase().includes(q) ||
        String(item.Id).includes(q);
    });
  });

  clearFilters(): void {
    this.query.set('');
    this.statusFilter.set('');
    this.companyFilter.set('');
  }

  constructor(private readonly api: PermitUserService) {}
  ngOnInit(): void {
    this.api.search({ Page: 1, PageSize: 50 }).subscribe({
      next: items => { this.items.set(items); this.loading.set(false); },
      error: err => { this.error.set(err?.error?.Message ?? err?.error?.message ?? 'permit.loadError'); this.loading.set(false); }
    });
  }
}
